import { Autocomplete } from '@mantine/core';
import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form';
import { useGetTeamValueSuggestionsQuery } from '@/common/graphql';
import { ApiUtils } from '@/common/utils';

interface TeamAutocompleteFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  error?: string;
  autoComplete?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  'data-1p-ignore'?: boolean;
}

// Shared by JobInformationFormFields.tsx (Add Employee) and JobHistoryForm.tsx (Job History edit
// modal) - both bind the same free-text `team` field to a suggestions-backed Autocomplete that
// still accepts a genuinely new typed value (FR-017/FR-018, research.md #7/#8). Mantine's
// Autocomplete.onChange receives the raw string value directly, not a native change event, so the
// field is bound via Controller rather than register().
export const TeamAutocompleteField = <TFieldValues extends FieldValues>({
  control,
  name,
  error,
  autoComplete,
  className,
  disabled,
  placeholder = 'Team',
  ...rest
}: TeamAutocompleteFieldProps<TFieldValues>) => {
  const { data } = useGetTeamValueSuggestionsQuery({
    onError: apolloError => {
      ApiUtils.handleApiError(apolloError);
    },
  });
  const teamValueSuggestions = data?.teamValueSuggestions ?? [];

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange, onBlur, ref, name: fieldName } }) => (
        <Autocomplete
          {...rest}
          ref={ref}
          autoComplete={autoComplete}
          className={className}
          data={teamValueSuggestions}
          disabled={disabled}
          error={error}
          label="Team"
          name={fieldName}
          placeholder={placeholder}
          // FieldPath<TFieldValues> is not narrowed to string-valued paths, so field.value's type
          // (FieldPathValue<TFieldValues, TName>) is an unconstrained generic here - both callers
          // pass a `team: string | null` field, so this cast is safe in practice even though the
          // type system can't verify it structurally.
          value={(value as unknown as string | null | undefined) ?? ''}
          onBlur={onBlur}
          onChange={newValue => onChange(newValue)}
        />
      )}
    />
  );
};
